import type { DiceSource } from './dice.js';
import type { DieFace } from './types.js';

export interface ScriptedDiceOptions {
  /**
   * Optional fallback used when the queue is empty. If omitted, an empty
   * queue throws — callers that rely on canned rolls fail loudly instead of
   * silently producing random outcomes.
   */
  fallback?: DiceSource;
}

/**
 * DiceSource backed by a FIFO queue. Callers enqueue canned rolls; the engine
 * drains one on each engine.roll(). Used by the tutorial (scripted lessons)
 * and by session replay (the producer's logged dice).
 */
export class ScriptedDice implements DiceSource {
  private readonly queue: Array<{ d1: DieFace; d2: DieFace }> = [];
  private readonly fallback: DiceSource | undefined;

  constructor(opts: ScriptedDiceOptions = {}) {
    this.fallback = opts.fallback;
  }

  enqueue(d1: number, d2: number) {
    this.queue.push({ d1: d1 as DieFace, d2: d2 as DieFace });
  }

  clear() {
    this.queue.length = 0;
  }

  roll(): { d1: DieFace; d2: DieFace } {
    const next = this.queue.shift();
    if (next) return next;
    if (this.fallback) return this.fallback.roll();
    throw new Error('ScriptedDice queue empty and no fallback configured');
  }
}
